import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { AsyncJobData } from './hooks/useAsyncJobPolling';
import { useFtueHost } from './host';
import { updatePreferredModel } from './onboarding-api';

type PlaybookSummary = { _id: string; name: string; stage: string; library?: boolean };

export interface OnboardingStartConfig {
  playbookId: string;
  name: string;
  paperType: 'first_party' | 'third_party';
  contractType: string;
  isExisting: boolean;
}

export interface ResumableExtraction {
  jobId: string;
  playbookId?: string;
  paperType?: 'first_party' | 'third_party';
  contractType?: string;
  filenames?: Array<string>;
}

export interface OnboardingContextValue {
  isFirstTimeUser: boolean;
  isCheckingOnboarding: boolean;
  onboardingStartStep: number;
  onboardingInitialConfig: OnboardingStartConfig | null;
  isReturningUser: boolean;
  playbooks: Array<{ _id: string; name: string; stage: string; library?: boolean }>;
  playbookRefreshTrigger: number;
  newlyCreatedPlaybookId: string | null;
  /** Set when an `extract_rules` async job was found still running (or just
   * finished) from a previous session, so the upload step can offer to
   * resume watching it instead of forcing the user to resubmit. */
  resumableExtraction: ResumableExtraction | null;
  /** True for exactly one mount of OnboardingUpload after `resumeExtraction()`
   * is called: the user already chose Resume on the global banner, so the
   * wizard's own Upload step should start polling immediately instead of
   * asking Resume/Discard a second time. Consumed (reset to false) via
   * `acknowledgeAutoResume`. */
  autoResumeExtraction: boolean;
  /** Mark `autoResumeExtraction` as consumed. Call once the upload step has
   * started (or decided not to start) the auto-resume poll, so a later
   * remount of the same OnboardingUpload instance doesn't re-trigger it. */
  acknowledgeAutoResume: () => void;
  /** Stop offering the current resumable extraction (e.g. it was
   * successfully resumed to completion). Does NOT cancel the backend job --
   * see `discardResumableExtraction` for that. */
  clearResumableExtraction: () => void;
  completeOnboarding: (newPlaybookId?: string) => void;
  startOnboarding: (step?: number, config?: OnboardingStartConfig, isReturning?: boolean) => void;
  /** Jump a returning user into the wizard's Upload step to resume
   * `resumableExtraction`, reconstructing enough config from the job's own
   * context (and the playbooks list, if it names an existing playbook), and
   * flag it as already-decided via `autoResumeExtraction` so the upload step
   * starts polling immediately rather than showing its own Resume/Discard
   * prompt again. No-op if there is no resumable extraction. */
  resumeExtraction: () => void;
  /** Cancel an async job by id and, only on a confirmed cancel, clear it
   * from `resumableExtraction` state if it's the one currently held there.
   * Takes the job id explicitly (rather than reading `resumableExtraction`
   * internally) so a caller rendering a job passed in as a prop -- which is
   * not guaranteed to be in sync with this context's own state -- discards
   * the right job. Returns whether the cancel succeeded so callers can
   * surface a retry instead of silently treating a failed request as if
   * the job were gone. */
  discardResumableExtraction: (jobId: string) => Promise<boolean>;
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

/**
 * Entry gate for the FTUE. Host-agnostic: gates on an injected `isReady` (the host's
 * "authenticated & session loaded" signal) and namespaces its localStorage flags by
 * `storageNamespace` (add-in: the tenant host; web: the tenant slug). Playbook fetches
 * go through the adapter's `bbAxios` — never a raw token.
 */
export function OnboardingProvider({
  isReady,
  storageNamespace,
  children,
}: {
  isReady: boolean;
  storageNamespace: string;
  children: ReactNode;
}) {
  const { bbAxios } = useFtueHost();
  const [isFirstTimeUser, setIsFirstTimeUser] = useState(false);
  const [isCheckingOnboarding, setIsCheckingOnboarding] = useState(true);
  const [onboardingStartStep, setOnboardingStartStep] = useState(0);
  const [onboardingInitialConfig, setOnboardingInitialConfig] =
    useState<OnboardingStartConfig | null>(null);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [playbooks, setPlaybooks] = useState<Array<PlaybookSummary>>([]);
  const [playbookRefreshTrigger, setPlaybookRefreshTrigger] = useState(0);
  const [newlyCreatedPlaybookId, setNewlyCreatedPlaybookId] = useState<string | null>(null);
  const [resumableExtraction, setResumableExtraction] = useState<ResumableExtraction | null>(null);
  const [autoResumeExtraction, setAutoResumeExtraction] = useState(false);

  const storageKey = `onboarding_complete_${storageNamespace}`;
  const forceKey = `force_onboarding_${storageNamespace}`;

  // The host's bbAxios is a fresh object on every auth-state change (e.g. a
  // background token refresh) even though isReady/storageKey/forceKey stay
  // the same -- pin it in a ref so those refreshes can't re-run the
  // FTUE-eligibility check below and clobber a since-started flow (see
  // hasStartedFlowRef).
  const bbAxiosRef = useRef(bbAxios);
  bbAxiosRef.current = bbAxios;

  // Set by startOnboarding (e.g. resumeExtraction, "Improve playbook"): once
  // the user has actively entered the flow, the eligibility check below must
  // never downgrade isFirstTimeUser back to false out from under them --
  // its job is initial detection on mount, not continuous re-validation.
  const hasStartedFlowRef = useRef(false);

  useEffect(() => {
    if (!isReady) {
      setIsCheckingOnboarding(false);
      return;
    }

    // Force key always takes precedence
    if (localStorage.getItem(forceKey)) {
      localStorage.removeItem(forceKey);
      localStorage.removeItem(storageKey);
      hasStartedFlowRef.current = true;
      setIsFirstTimeUser(true);
      setIsCheckingOnboarding(false);
      return;
    }

    // Always check playbook count to determine FTUE eligibility
    // This allows different users on the same host to each get FTUE if they have 0 playbooks
    const checkPlaybookCount = async () => {
      try {
        console.log('[OnboardingContext] Checking playbook count for FTUE...');
        const { data: playbooks } =
          await bbAxiosRef.current.get<Array<PlaybookSummary>>('/api/trainingmodels');

        // Store playbooks for use by other components
        setPlaybooks(playbooks);

        console.log('[OnboardingContext] Total playbooks fetched:', playbooks.length);

        // Count non-library, non-backup playbooks
        const userPlaybookCount = playbooks.filter(
          (p) => p.library !== true && p.stage !== 'backup'
        ).length;

        console.log(
          '[OnboardingContext] User playbook count (non-library, non-backup):',
          userPlaybookCount
        );

        // Show FTUE if user has no playbooks (ignores localStorage completion flag)
        if (userPlaybookCount === 0) {
          console.log('[OnboardingContext] Setting isFirstTimeUser = true (no playbooks)');
          localStorage.removeItem(storageKey); // Clear any stale completion flag
          hasStartedFlowRef.current = true;
          setIsFirstTimeUser(true);
        } else {
          console.log('[OnboardingContext] User has playbooks, skipping FTUE');
          // User has playbooks - never show FTUE regardless of localStorage
          // Set completion flag if missing to prevent future checks
          if (!localStorage.getItem(storageKey)) {
            localStorage.setItem(storageKey, 'true');
          }
          if (!hasStartedFlowRef.current) {
            setIsFirstTimeUser(false);
          }
        }
      } catch (error) {
        console.error('[OnboardingContext] Error fetching playbooks:', error);
        // If the endpoint fails, check localStorage as fallback
        if (!localStorage.getItem(storageKey)) {
          hasStartedFlowRef.current = true;
          setIsFirstTimeUser(true);
        }
      } finally {
        setIsCheckingOnboarding(false);
      }
    };

    void checkPlaybookCount();
  }, [isReady, storageKey, forceKey]);

  // Refresh playbooks list when playbookRefreshTrigger changes (e.g., after creating a new playbook)
  useEffect(() => {
    if (!isReady || playbookRefreshTrigger === 0) {
      return;
    }

    const refreshPlaybooks = async () => {
      try {
        console.log(
          '[OnboardingContext] Refreshing playbooks list (trigger:',
          playbookRefreshTrigger,
          ')'
        );
        const { data: playbooks } =
          await bbAxios.get<Array<PlaybookSummary>>('/api/trainingmodels');
        setPlaybooks(playbooks);
        console.log('[OnboardingContext] Playbooks list refreshed, count:', playbooks.length);
      } catch (error) {
        console.error('[OnboardingContext] Error refreshing playbooks:', error);
      }
    };

    void refreshPlaybooks();
  }, [playbookRefreshTrigger, isReady, bbAxios]);

  // Check for an extraction that was still running (or just finished) when the
  // user last left the add-in, so OnboardingUpload can offer to resume it
  // instead of the user resubmitting a document that may take 60+ minutes to
  // re-process. Kept as its own effect (rather than folded into the FTUE
  // eligibility check above) so a failure here can't affect FTUE gating.
  useEffect(() => {
    if (!isReady) {
      return;
    }

    const checkForResumableExtraction = async () => {
      try {
        const { data: activeJobs } = await bbAxiosRef.current.get<Array<AsyncJobData>>(
          '/api/v2/async-jobs/mine/active'
        );
        const job = activeJobs.find((j) => j.context?.operationType === 'extract_rules');
        if (job) {
          const context = job.context as {
            playbookId?: string;
            paperType?: 'first_party' | 'third_party';
            contractType?: string;
            filenames?: Array<string>;
          };
          setResumableExtraction({
            jobId: job._id,
            playbookId: context.playbookId,
            paperType: context.paperType,
            contractType: context.contractType,
            filenames: context.filenames,
          });
        }
      } catch (error) {
        console.error('[OnboardingContext] Failed to check for resumable extraction job:', error);
      }
    };

    void checkForResumableExtraction();
  }, [isReady]);

  const clearResumableExtraction = useCallback(() => {
    setResumableExtraction(null);
  }, []);

  const discardResumableExtraction = useCallback(
    async (jobId: string) => {
      try {
        await bbAxios.patch(`/api/v2/async-jobs/${jobId}/cancel`);
        setResumableExtraction((prev) => (prev?.jobId === jobId ? null : prev));
        return true;
      } catch (error) {
        console.error('[OnboardingContext] Failed to discard resumable extraction job:', error);
        return false;
      }
    },
    [bbAxios]
  );

  const completeOnboarding = useCallback(
    async (newPlaybookId?: string) => {
      localStorage.setItem(storageKey, 'true');
      localStorage.removeItem(forceKey);
      hasStartedFlowRef.current = false;
      setIsFirstTimeUser(false);
      setIsReturningUser(false);
      setOnboardingStartStep(0);
      setOnboardingInitialConfig(null);

      // Trigger refresh of PlaybooksPage
      setPlaybookRefreshTrigger((prev) => prev + 1);

      // Store the newly created playbook ID for App component to pick up
      if (newPlaybookId) {
        setNewlyCreatedPlaybookId(newPlaybookId);
      }

      // If a new playbook was created, always set it as preferred to make it the active playbook
      if (newPlaybookId) {
        try {
          console.log(
            '[OnboardingContext] Setting newly created playbook as preferred:',
            newPlaybookId
          );
          await updatePreferredModel(bbAxios, newPlaybookId);
        } catch (error) {
          console.error('[OnboardingContext] Error setting preferred playbook:', error);
        }
      }
    },
    [storageKey, forceKey, bbAxios]
  );

  const startOnboarding = useCallback(
    (step = 0, config?: OnboardingStartConfig, isReturning = false) => {
      hasStartedFlowRef.current = true;
      setOnboardingStartStep(step);
      setOnboardingInitialConfig(config ?? null);
      setIsReturningUser(isReturning);
      setIsFirstTimeUser(true);
    },
    []
  );

  const resumeExtraction = useCallback(() => {
    if (!resumableExtraction) {
      return;
    }
    const resumedPlaybook = playbooks.find((p) => p._id === resumableExtraction.playbookId);
    setAutoResumeExtraction(true);
    startOnboarding(
      3,
      {
        playbookId: resumableExtraction.playbookId ?? '',
        name: resumedPlaybook?.name ?? 'your playbook',
        paperType: resumableExtraction.paperType ?? 'third_party',
        contractType: resumableExtraction.contractType ?? 'nda',
        isExisting: !!resumedPlaybook,
      },
      true
    );
  }, [resumableExtraction, playbooks, startOnboarding]);

  const acknowledgeAutoResume = useCallback(() => {
    setAutoResumeExtraction(false);
  }, []);

  return (
    <OnboardingContext.Provider
      value={{
        isFirstTimeUser,
        isCheckingOnboarding,
        onboardingStartStep,
        onboardingInitialConfig,
        isReturningUser,
        playbooks,
        playbookRefreshTrigger,
        newlyCreatedPlaybookId,
        resumableExtraction,
        autoResumeExtraction,
        acknowledgeAutoResume,
        clearResumableExtraction,
        discardResumableExtraction,
        completeOnboarding,
        startOnboarding,
        resumeExtraction,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding(): OnboardingContextValue {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}
